import type { ColumnState, SortModelItem } from 'ag-grid-community'
import { isArray, isPlainObject, pipe, sortBy, uniqueBy } from 'remeda'

// Public types ----------------------------------------------------------------

export type SortDirection =
  | 'asc'
  | 'desc'
  | 'asc_nulls_first'
  | 'asc_nulls_last'
  | 'desc_nulls_first'
  | 'desc_nulls_last'

export type GridSort = Readonly<{ field: string; direction: SortDirection }>
export type SortColumn = Readonly<{
  colId: string
  sortField?: string
  visible?: boolean
  sort?: ColumnState['sort']
  sortIndex?: number | null
}>

// Constants -------------------------------------------------------------------

const DIRECTIONS: ReadonlySet<string> = new Set([
  'asc',
  'desc',
  'asc_nulls_first',
  'asc_nulls_last',
  'desc_nulls_first',
  'desc_nulls_last'
])

// Private functions -----------------------------------------------------------

/** Accept only the backend's closed ordering contract. */
const isGridSort = (value: unknown): value is GridSort =>
  isPlainObject(value) &&
  typeof value.field === 'string' &&
  value.field.length > 0 &&
  typeof value.direction === 'string' &&
  DIRECTIONS.has(value.direction)

/** Display the base direction without changing backend null ordering. */
const displayDirection = (direction: SortDirection): 'asc' | 'desc' =>
  direction.startsWith('asc') ? 'asc' : 'desc'

/** Choose one visible, active alias per backend field, following new Shift sorts. */
const sortRepresentatives = (
  columns: readonly SortColumn[]
): ReadonlyMap<string | undefined, string> => {
  const preferred = pipe(
    columns,
    sortBy(
      (column) => column.visible === false,
      (column) => !column.sort,
      (column) => -(column.sortIndex ?? -1)
    ),
    uniqueBy((column) => column.sortField)
  )

  return new Map(preferred.map(({ sortField, colId }) => [sortField, colId]))
}

// Public functions ------------------------------------------------------------

/** Read application metadata without widening AG Grid's column contract. */
export const gridSortField = (context: unknown): string | undefined =>
  isPlainObject(context) &&
  typeof context.sortField === 'string' &&
  context.sortField.length > 0
    ? context.sortField
    : undefined

/** Reject malformed sorting rather than interpreting it as a reset. */
export const normalizeGridSorting = (
  value: unknown
): readonly GridSort[] | undefined =>
  isArray(value) && value.every(isGridSort)
    ? value.map(({ field, direction }) => ({ field, direction }))
    : undefined

/** Project each backend sort once, retaining hidden and dropdown-only priorities. */
export const sortingColumnState = (
  columns: readonly SortColumn[],
  sorting: readonly GridSort[]
): ColumnState[] => {
  const representatives = sortRepresentatives(columns)

  return columns.map(({ colId, sortField }) => {
    const sortIndex =
      representatives.get(sortField) === colId
        ? sorting.findIndex((sort) => sort.field === sortField)
        : -1
    const direction = sorting[sortIndex]?.direction

    return {
      colId,
      sort: direction === undefined ? null : displayDirection(direction),
      sortIndex: direction === undefined ? null : sortIndex
    }
  })
}

/** Derive the native sort model without treating dropdown-only priority gaps as changes. */
export const nativeSortingModel = (
  state: readonly ColumnState[]
): readonly SortModelItem[] =>
  sortBy(
    state.filter((column) => column.sort === 'asc' || column.sort === 'desc'),
    (column) => column.sortIndex ?? 0
  ).flatMap(({ colId, sort }) =>
    sort === 'asc' || sort === 'desc' ? [{ colId, sort }] : []
  )

/** Compare the grid request with the accepted projected order, not query input. */
export const matchesAcceptedSorting = (
  model: readonly SortModelItem[],
  state: readonly ColumnState[]
): boolean => {
  const expected = nativeSortingModel(state)

  return (
    model.length === expected.length &&
    model.every(
      (sort, index) =>
        sort.colId === expected[index]?.colId &&
        sort.sort === expected[index]?.sort
    )
  )
}
